import { useCallback, useMemo } from 'react';
import type { FieldMapping, MappingResponse } from '../../services/api';
import { collectMappingSourceFieldNames } from '../graph/transformAnalysis';
import type {
  CanvasSelection,
  LineageChain,
  LineageNode,
  LineageStep,
} from '../types';

export interface UseLineageComputationOptions {
  selection: CanvasSelection | null;
  mappingResponse?: MappingResponse;
}

export function useLineageComputation({
  selection,
  mappingResponse,
}: UseLineageComputationOptions) {
  const buildChainFromMapping = useCallback(
    (mapping: FieldMapping): LineageChain => {
      const sourceFields = collectMappingSourceFieldNames(mapping);

      const sources: LineageNode[] = sourceFields.map((f) => ({
        label: f,
        qualifiedName: `${mapping.source_file}.${f}`,
        table: mapping.source_file ?? '',
        field: f,
        kind: 'source' as const,
      }));

      const target: LineageNode = {
        label: mapping.target_field ?? '',
        qualifiedName: `${mapping.target_table}.${mapping.target_field}`,
        table: mapping.target_table ?? '',
        field: mapping.target_field ?? '',
        kind: 'target' as const,
        confidence: mapping.confidence,
      };

      const mappingType = (mapping.transformation_type ?? 'direct').toLowerCase();
      const isNonDirectMappingType = mappingType !== 'direct';

      const steps: LineageStep[] = (mapping.transformation_steps ?? [])
        .filter(Boolean)
        .map((step) => {
          const rawOp = (step.operation || step.kind || 'direct').toLowerCase();
          const effectiveOp =
            rawOp === 'direct' && isNonDirectMappingType ? mappingType : rawOp;
          return {
            operation: effectiveOp,
            expression: step.expression,
            description: step.description,
            parameters: step.parameters,
            confidence: step.confidence ?? mapping.confidence ?? 0,
            aiGenerated: step.ai_generated ?? false,
          };
        });

      if (steps.length === 0 && isNonDirectMappingType) {
        steps.push({
          operation: mapping.operation || mappingType,
          expression: mapping.transformation ?? undefined,
          description: mapping.explanation ?? undefined,
          parameters: mapping.parameters,
          confidence: mapping.confidence ?? 0,
          aiGenerated: true,
        });
      }

      return {
        mappingId: mapping.mapping_id,
        sources,
        target,
        steps,
        overallConfidence: mapping.confidence ?? 0,
        explanation: mapping.explanation,
        transformationType: mapping.transformation_type,
      };
    },
    [],
  );

  const computedLineageChains = useMemo((): LineageChain[] => {
    if (!selection || selection.kind !== 'node' || !mappingResponse?.mappings) return [];
    const node = selection.node;
    const nodeKind = node.data.kind;
    const nodeTable = (node.data.table ?? '').toLowerCase();
    const nodeField = (node.data.field ?? '').toLowerCase();
    const mappingIds = node.data.mappingIds ?? [];

    if (nodeKind === 'targetField') {
      return mappingResponse.mappings
        .filter(
          (m) =>
            (m.target_table ?? '').toLowerCase() === nodeTable &&
            (m.target_field ?? '').toLowerCase() === nodeField,
        )
        .map(buildChainFromMapping);
    }

    if (nodeKind === 'sourceField') {
      return mappingResponse.mappings
        .filter((m) => {
          if ((m.source_file ?? '').toLowerCase() !== nodeTable) return false;
          const fields = (m.source_field ?? '')
            .split(',')
            .map((s) => s.trim().toLowerCase());
          return fields.some((f) => f === nodeField);
        })
        .map(buildChainFromMapping);
    }

    if (mappingIds.length > 0) {
      const idSet = new Set(mappingIds);
      return mappingResponse.mappings
        .filter((m) => idSet.has(m.mapping_id))
        .map(buildChainFromMapping);
    }

    return [];
  }, [selection, mappingResponse, buildChainFromMapping]);

  const computedLineage = useMemo(() => {
    const upstream = computedLineageChains.flatMap((c) =>
      c.sources.map((s) => s.qualifiedName),
    );
    const downstream = computedLineageChains.flatMap((c) => [
      c.target.qualifiedName,
    ]);
    return {
      upstream: Array.from(new Set(upstream)) as string[],
      downstream: Array.from(new Set(downstream)) as string[],
    };
  }, [computedLineageChains]);

  return {
    buildChainFromMapping,
    computedLineageChains,
    computedLineage,
  };
}
